import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import type {
  IconFeatureGridBlockItem,
  IconFeatureGridBlockLink,
  IIconFeatureGridBlockProps,
} from "./IconFeatureGridBlock.props";
import { RichTextHtml } from "../../RichTextHtml";
import { KaajAction } from "../_shared/KaajAction";

const sectionThemeClass = "text-[#151927]";

const gridClasses = {
  3: "md:grid-cols-2 lg:grid-cols-3",
  4: "md:grid-cols-2 xl:grid-cols-4",
} satisfies Record<NonNullable<IIconFeatureGridBlockProps["columns"]>, string>;

const tone = {
  eyebrow: "border-[#CAD7FF] bg-white/92 text-[#2144FF] shadow-[0_10px_26px_rgba(33,68,255,0.1)]",
  subheading: "text-[#4B5568]",
  card: "border-[#D9E1FF] bg-white text-[#151927] shadow-[0_22px_68px_rgba(33,68,255,0.08)] hover:border-[#BFD0FF] hover:shadow-[0_30px_78px_rgba(33,68,255,0.14)]",
  cardTitle: "text-[#151927]",
  cardText: "text-[#4B5568]",
  icon: "border-[#C9D3FF] bg-[#EEF2FF] text-[#2144FF]",
  orbit: "border-[#D9E1FF] bg-[#F7F9FF]",
  link: "text-[#2144FF] decoration-[#2144FF]/20 group-hover/card:text-[#132EDB] group-hover/card:decoration-[#132EDB]",
  focusOffset: "focus-visible:ring-offset-white",
};

const signalClasses = ["bg-[#2144FF]", "bg-[#55D6C2]", "bg-[#FFD86B]", "bg-[#FFEAF9]"];

const getSafeRel = (link: IconFeatureGridBlockLink) =>
  link.target === "_blank" || link.href.startsWith("http") ? "noopener noreferrer" : undefined;

const FeatureCard = ({ index, item }: { index: number; item: IconFeatureGridBlockItem }) => {
  return (
    <article
      className={cn(
        "group/card relative isolate flex h-full min-h-[232px] flex-col overflow-hidden rounded-[1.75rem] border p-5 transition-[border-color,box-shadow,transform] duration-300 motion-reduce:transition-none motion-safe:hover:-translate-y-1 sm:p-6",
        tone.card,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute -right-10 -top-10 -z-10 size-32 rounded-full border opacity-80 transition-transform duration-300 group-hover/card:scale-110 motion-reduce:transition-none",
          tone.orbit,
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "absolute left-5 top-0 h-1.5 w-16 rounded-b-full sm:left-6",
          signalClasses[index % signalClasses.length],
        )}
      />

      <div className="relative flex flex-1 flex-col">
        <div className="flex flex-col gap-5">
          <span
            className={cn(
              "flex size-12 shrink-0 items-center justify-center rounded-2xl border shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]",
              tone.icon,
            )}
          >
            <Icon className="size-6" name={item.icon} />
          </span>

          <h3
            className={cn(
              "min-w-0 text-balance text-xl font-extrabold leading-tight tracking-[-0.025em] [overflow-wrap:anywhere]",
              tone.cardTitle,
            )}
          >
            {item.heading}
          </h3>
        </div>

        <RichTextHtml
          className={cn(
            "mt-4 text-pretty text-sm leading-6 [overflow-wrap:anywhere] sm:text-base sm:leading-7",
            tone.cardText,
          )}
          html={item.subheading}
        />
        {item.link ? (
          <span className="mt-auto pt-5">
            <span
              className={cn(
                "inline-flex min-h-11 items-center gap-2 rounded-full text-sm font-extrabold underline underline-offset-4 transition-[color,decoration-color,transform] duration-200 motion-reduce:transition-none motion-safe:group-hover/card:translate-x-0.5",
                tone.link,
              )}
            >
              {item.link.text}
              <span aria-hidden="true">→</span>
            </span>
          </span>
        ) : null}
      </div>
    </article>
  );
};

export const IconFeatureGridBlock = (props: IIconFeatureGridBlockProps) => {
  const {
    anchorId,
    buttons,
    columns: columnsProp,
    eyebrow,
    heading,
    items = [],
    parentClass,
    spacing: spacingProp,
    subheading,
    variant = "vertical",
  } = props;

  const sectionId = anchorId ?? undefined;
  const columns = columnsProp ?? 3;
  const headingId = heading && sectionId ? `${sectionId}-heading` : undefined;
  const Link = resolveKaajPrimitive("Link");
  const hasHeader = Boolean(eyebrow || heading || subheading);
  const isVertical = variant === "vertical";
  const alignment = isVertical ? "center" : "left";

  if (!hasHeader && !items.length && !buttons?.length) {
    return null;
  }

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative isolate overflow-hidden",
        sectionThemeClass,
        getKaajSectionSpacing(spacingProp),
        parentClass,
      )}
      id={sectionId}
    >
      <div className="kaaj-container relative">
        <div
          className={cn(
            "relative grid min-w-0",
            isVertical ? "gap-0" : "gap-8 lg:gap-10 xl:gap-12",
            !isVertical && hasHeader && items.length
              ? "lg:grid-cols-[minmax(15rem,0.38fr)_minmax(0,1fr)] xl:gap-16"
              : "grid-cols-1",
          )}
        >
          {hasHeader ? (
            <div
              className={cn(
                "flex min-w-0 flex-col gap-4",
                isVertical ? "mb-10 max-w-4xl lg:mb-14" : "max-w-4xl",
                alignment === "center"
                  ? "mx-auto items-center text-center"
                  : "items-start text-left",
              )}
            >
              {eyebrow ? (
                <p
                  className={cn(
                    "inline-flex items-center rounded-full border px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.18em] [overflow-wrap:anywhere]",
                    tone?.eyebrow,
                  )}
                >
                  {eyebrow}
                </p>
              ) : null}
              {heading ? (
                <h2 className="kaaj-section-heading" id={headingId}>
                  {heading}
                </h2>
              ) : null}
              {subheading ? (
                <RichTextHtml
                  className={cn(
                    "text-pretty text-base leading-8 sm:text-lg",
                    isVertical ? "max-w-2xl" : "max-w-xl",
                    tone.subheading,
                  )}
                  html={subheading}
                />
              ) : null}
            </div>
          ) : null}

          {items.length ? (
            <div
              className={cn(
                "grid min-w-0 grid-cols-1 gap-5 lg:gap-6",
                !isVertical && "self-start",
                gridClasses[columns],
              )}
            >
              {items.map((item, index) =>
                item.link ? (
                  <Link
                    aria-label={item.link.ariaLabel || item.link.text}
                    className={cn(
                      "block h-full rounded-[1.75rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2144FF] focus-visible:ring-offset-4",
                      tone.focusOffset,
                    )}
                    href={item.link.href}
                    key={`${item.heading}-${index}`}
                    rel={getSafeRel(item.link)}
                    target={item.link.target}
                  >
                    <FeatureCard index={index} item={item} />
                  </Link>
                ) : (
                  <FeatureCard index={index} item={item} key={`${item.heading}-${index}`} />
                ),
              )}
            </div>
          ) : null}
        </div>

        {buttons?.length ? (
          <div className={cn("mt-8 flex flex-wrap gap-3", isVertical && "justify-center")}>
            {buttons.map((button, index) => (
              <KaajAction
                ariaLabel={button.ariaLabel}
                href={button.href}
                key={`${button.href}-${button.text}-${index}`}
                label={button.text}
                target={button.target}
                variant={button.style}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

IconFeatureGridBlock.displayName = "Block::IconFeatureGridBlock";
